import { useState, useEffect } from "react";

import type { SeriesColumnOptions } from "highcharts";
import styled from "styled-components";

import { ColumnChartGraph, LEGEND_POSITIONS } from "@similarweb/pro-logic-components";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { CHART_COLORS } from "app/constants/ChartColors";
import { translate } from "app/filters/i18nFilter";

import { applyLegendToggleWithColorStack } from "app/utils/chart/applyLegendToggleWithColorStack";

import type { ITransformedSerpItem } from "./serpDataTransform";
import { getChartConfig } from "./serpFeaturesCompositionGraphConfig";

const MAX_SELECTED_LEGENDS = 10;

const ColumnChartWrapper = styled.div`
    padding-right: 20px;
`;

type SeriesItem = SeriesColumnOptions & { id: string; color?: string; name: string };

interface SERPFeaturesCompositionProps {
    data: ITransformedSerpItem[];
    isLoading: boolean;
    isError: boolean;
    hasData: boolean;
    tooltip: string;
    acquire: () => string;
    release: (color: string) => void;
}

export const SERPFeaturesComposition = ({
    data,
    isLoading,
    isError,
    hasData,
    tooltip,
    acquire,
    release,
}: SERPFeaturesCompositionProps) => {
    const [chartItems, setChartItems] = useState<ITransformedSerpItem[]>(data || []);

    useEffect(() => {
        if (data) {
            setChartItems(data);
        }
    }, [data]);

    const handleLegendToggle = (toggledItems: { id: string }[]) => {
        setChartItems((prevItems) =>
            applyLegendToggleWithColorStack(prevItems, toggledItems, {
                acquire,
                release,
                hiddenSeriesColor: CHART_COLORS.fallbackColor,
            }),
        );
    };

    const noDataObject = isError
        ? {
              title: translate("keyword.research.search.ads.table.error.title"),
              subtitle: translate("keyword.research.search.ads.table.error.subtitle"),
          }
        : {
              title: translate("common.noresults"),
              subtitle: translate("keyword.research.search.ads.table.no.data.subtitle"),
          };

    return (
        <WidgetContainer
            withBorder={true}
            title={translate("website.analysis.seo.overview.serp_features_composition.title")}
            tooltip={tooltip}
            isLoading={isLoading}
            noData={!isLoading ? !hasData || isError : false}
            noDataTitle={noDataObject.title}
            noDataSubtitle={noDataObject.subtitle}
            loaderWidth={"100%"}
            loaderHeight={280}
            minWidth={"100%"}
        >
            <ColumnChartWrapper data-automation="serp-features-composition-graph">
                {chartItems.length > 0 ? (
                    <ColumnChartGraph<SeriesItem>
                        data={chartItems}
                        graphConfig={getChartConfig()}
                        isControlledGraph={true}
                        shouldPresentLegend={true}
                        legendPosition={LEGEND_POSITIONS.RIGHT}
                        maxLegendItemsSelected={MAX_SELECTED_LEGENDS}
                        isLegendSearch={true}
                        legendSearchPlaceholder={translate(
                            "website.analysis.seo.overview.serp_features_composition.legend.search.placeholder",
                        )}
                        disabledLegendItemTooltip={translate(
                            "website.analysis.seo.overview.serp_features_composition.legend.disabled.tooltip",
                        )}
                        legendsMaxWidth={250}
                        legendsMinWidth={250}
                        legendsHeight={"180px"}
                        legendsWithValueMaxWidth={250}
                        legendHeaders={[
                            translate("website.analysis.seo.overview.serp_features_composition.legend.headers"),
                        ]}
                        isToggleAllEnabled
                        shouldPresentValueAtLegend={true}
                        legendToggleHandlerCallback={handleLegendToggle}
                    />
                ) : null}
            </ColumnChartWrapper>
        </WidgetContainer>
    );
};
